import { Component, OnInit, OnDestroy, inject } from '@angular/core';
import { RouterModule, Router, NavigationEnd } from '@angular/router';
import { CommonModule } from '@angular/common';
import { FooterComponent } from '@hypenation/shared';
import { AuthStateService } from '@hypenation/auth';
import { CartStateService, Cart, SessionService } from '@hypenation/tickets';
import { CustomerProfileService } from '@hypenation/client-portal';
import { NzIconModule } from 'ng-zorro-antd/icon';
import { NzBadgeModule } from 'ng-zorro-antd/badge';
import { Subscription, filter } from 'rxjs';

interface NavItem {
  label: string;
  route: string;
  icon: string;
  exact?: boolean;
}

@Component({
  standalone: true,
  imports: [
    RouterModule,
    CommonModule,
    FooterComponent,
    NzIconModule,
    NzBadgeModule,
  ],
  selector: 'hypenation-root',
  templateUrl: './app.component.html',
  styleUrl: './app.component.less',
})
export class AppComponent implements OnInit, OnDestroy {
  private readonly authStateService = inject(AuthStateService);
  private readonly router = inject(Router);
  private readonly cartStateService = inject(CartStateService);
  private readonly sessionService = inject(SessionService);
  private readonly customerProfileService = inject(CustomerProfileService);
  private authSub?: Subscription;
  private routerSub?: Subscription;
  private cartSub?: Subscription;

  mobileSidebarOpen = false;
  userEmail: string | null = null;
  userInitials = '';
  activeRoute = '';

  // Cart state
  cart: Cart | null = null;
  numberOfItems = 0;

  navItems: NavItem[] = [
    { label: 'Dashboard', route: '/', icon: 'appstore', exact: true },
    { label: 'Buy Tickets', route: '/events', icon: 'tag' },
    { label: 'Organisers', route: '/organisers', icon: 'team' },
    { label: 'My Orders', route: '/orders', icon: 'shopping' },
    // Ahead of My Tickets: an event is the unit a customer thinks in on the day, and it is where
    // the wallet balance and top-up live. My Tickets stays the flat list of every ticket.
    { label: 'My Events', route: '/my-events', icon: 'calendar' },
    { label: 'My Tickets', route: '/tickets', icon: 'qrcode' },
    { label: 'Wishlist', route: '/wishlist', icon: 'heart' },
  ];

  get isLoginPage(): boolean {
    return this.activeRoute === '/login';
  }

  ngOnInit(): void {
    this.authStateService.initialize('client-portal');
    this.checkAuth();
    this.loadCustomerProfile();
    this.activeRoute = this.router.url.split('?')[0];
    this.initSession();

    this.authSub = this.authStateService
      .onAuthStateChange()
      .subscribe((authed) => {
        this.checkAuth();
        if (authed) {
          this.loadCustomerProfile();
        } else {
          this.customerProfileService.clear();
        }
      });

    this.routerSub = this.router.events
      .pipe(filter((e): e is NavigationEnd => e instanceof NavigationEnd))
      .subscribe((e) => {
        this.activeRoute = e.urlAfterRedirects.split('?')[0];
        this.mobileSidebarOpen = false;
      });

    // Listen for cart updates (from add-to-cart in ViewOne, wishlist, etc.)
    this.cartSub = this.cartStateService.cartUpdate$.subscribe(() => {
      this.syncCartFromState();
    });
  }

  ngOnDestroy(): void {
    this.authSub?.unsubscribe();
    this.routerSub?.unsubscribe();
    this.cartSub?.unsubscribe();
  }

  initSession(): void {
    this.cartStateService.resolveCart().subscribe({
      next: (cart) => {
        this.cart = cart;
        this.numberOfItems = cart?.numberOfItems ?? 0;
      },
    });
  }

  private syncCartFromState(): void {
    const cart = this.cartStateService.cart();
    if (cart) {
      this.cart = cart;
      this.numberOfItems = cart.numberOfItems ?? 0;
    }
  }

  navigateToCart(): void {
    // Resume an in-flight checkout instead of routing to a now-empty cart.
    const pendingOrderNumber = this.sessionService.getPendingOrderNumber();
    if (pendingOrderNumber) {
      this.router.navigateByUrl('/checkout/' + pendingOrderNumber);
      return;
    }

    if (this.cart) {
      this.router.navigateByUrl('/cart/' + this.cart.uid);
    }
  }

  private loadCustomerProfile(): void {
    const email = this.authStateService.getUserEmail();
    if (email) {
      this.customerProfileService.loadProfile(email).subscribe();
    }
  }

  checkAuth(): void {
    this.userEmail = this.authStateService.getUserEmail();
    if (this.userEmail) {
      const parts = this.userEmail.split('@')[0].split('.');
      this.userInitials =
        parts.length >= 2
          ? (parts[0][0] + parts[1][0]).toUpperCase()
          : this.userEmail.substring(0, 2).toUpperCase();
    } else {
      this.userEmail = null;
      this.userInitials = '';
    }
  }

  isActive(item: NavItem): boolean {
    if (item.exact) return this.activeRoute === item.route;
    return this.activeRoute.startsWith(item.route);
  }

  toggleMobileSidebar(): void {
    this.mobileSidebarOpen = !this.mobileSidebarOpen;
  }

  logout(): void {
    this.authStateService.removeTokens();
    this.authStateService.removeUserEmail();
    this.customerProfileService.clear();
    this.userEmail = null;
    this.userInitials = '';
    this.router.navigate(['/login']);
  }
}
